import type { ListingModel, OrderItemModel, SkinGroup, TemplateModel } from '../../api/types';
import type { CartLine } from '../../hooks/useCart';

/*
 * Como uma skin vira item do GA4.
 *
 * O item_name é o nome da skin sem desgaste nem StatTrak/Souvenir, igual em
 * todas as etapas: é ele que liga "viu a lista" a "adicionou à sacola" no
 * relatório. O desgaste vai em item_variant, e StatTrak/Souvenir em item_brand.
 *
 * O catálogo guarda preço em yuan. Todo valor sai daqui em reais, pela mesma
 * cotação que o site mostra; sem cotação, o preço não é enviado, porque
 * registrar yuan como real seria pior do que não registrar.
 */

export interface ItemGA4 {
  item_id: string;
  item_name: string;
  item_variant?: string;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  price?: number;
  quantity?: number;
  index?: number;
}

let cnyParaBrl: number | null = null;

/** A cotação do momento. Chamado pela ponte do App quando ela carrega. */
export function definirCotacao(taxa: number | null): void {
  cnyParaBrl = typeof taxa === 'number' && taxa > 0 ? taxa : null;
}

const centavos = (v: number) => Math.round(v * 100) / 100;

export function reais(valorCny: number | null | undefined): number | undefined {
  if (valorCny === null || valorCny === undefined || cnyParaBrl === null) return undefined;
  return centavos(valorCny * cnyParaBrl);
}

const DESGASTE = /\s*\((Factory New|Minimal Wear|Field-Tested|Well-Worn|Battle-Scarred)\)\s*$/i;

function nomeBase(nome: string | null | undefined): string {
  return (nome ?? 'Skin').replace(DESGASTE, '').replace(/^StatTrak™\s*/i, '').replace(/^Souvenir\s*/i, '').trim();
}

function marca(nome: string | null | undefined, st?: boolean, souvenir?: boolean): string | undefined {
  if (st || /^StatTrak™/i.test(nome ?? '')) return 'StatTrak';
  if (souvenir || /^Souvenir/i.test(nome ?? '')) return 'Souvenir';
  return undefined;
}

/** Tira do objeto os campos sem valor: o GA4 mostra "(not set)" para vazio. */
function limpo(item: ItemGA4): ItemGA4 {
  return Object.fromEntries(Object.entries(item).filter(([, v]) => v !== undefined && v !== '')) as ItemGA4;
}

/** Card do catálogo agrupado: a skin, antes de escolher o desgaste. */
export function itemDoGrupo(g: SkinGroup, index?: number): ItemGA4 {
  return limpo({
    item_id: g.key,
    item_name: nomeBase(g.name),
    item_brand: marca(g.name, g.isStatTrak, g.isSouvenir),
    item_category: g.kind ?? undefined,
    item_category2: g.weaponCode ?? undefined,
    price: reais(g.minPrice),
    index,
  });
}

/** A skin num desgaste: a unidade que tem página de anúncios. */
export function itemDoTemplate(t: TemplateModel, index?: number): ItemGA4 {
  return limpo({
    item_id: String(t.id),
    item_name: nomeBase(t.name),
    item_variant: t.exterior?.pt,
    item_brand: marca(t.name, t.isStatTrak, t.isSouvenir),
    item_category: t.kind ?? undefined,
    item_category2: t.weaponCode ?? undefined,
    price: reais(t.price),
    index,
  });
}

/** Uma peça anunciada. O id é o do template: float e pattern não viram item. */
export function itemDoAnuncio(l: ListingModel): ItemGA4 {
  return limpo({
    item_id: l.templateId !== null ? String(l.templateId) : l.commodityNo,
    item_name: nomeBase(l.name),
    item_variant: l.exterior?.pt,
    item_brand: marca(l.name),
    price: reais(l.price),
    quantity: 1,
  });
}

/** Linha da sacola. Já está em reais: a sacola guarda o preço convertido. */
export function itemDaSacola(linha: CartLine): ItemGA4 {
  return limpo({
    item_id: String(linha.templateId),
    item_name: nomeBase(linha.name),
    item_variant: linha.exterior ?? undefined,
    item_brand: marca(linha.name),
    price: centavos(linha.unitBrl),
    quantity: linha.qty,
  });
}

/** Item de pedido que o cliente leva. Já está em reais. */
export function itemDoPedido(i: OrderItemModel): ItemGA4 {
  return limpo({
    item_id: i.templateId !== null ? String(i.templateId) : nomeBase(i.name),
    item_name: nomeBase(i.name),
    item_variant: i.exterior ?? undefined,
    item_brand: marca(i.name),
    price: centavos(i.unitBrl),
    quantity: i.qty,
  });
}

/** Soma dos itens em reais. Some se algum item não tiver preço. */
export function valorDosItens(items: ItemGA4[]): number | undefined {
  if (items.length === 0 || items.some((i) => i.price === undefined)) return undefined;
  return centavos(items.reduce((s, i) => s + (i.price ?? 0) * (i.quantity ?? 1), 0));
}

/** Só id e nome: onde a tela não tem o preço à mão (a estrela de favorito). */
export function itemPorId(templateId: number, nome: string | null | undefined): ItemGA4 {
  return limpo({ item_id: String(templateId), item_name: nomeBase(nome), item_brand: marca(nome) });
}
